import React from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { formatDateForSave, parseISOToDate } from 'utils/dateUtils'

export interface DateInputProps {
  id: string
  name: string
  label?: React.ReactNode
  labelClassName?: string
  value: string | null | undefined
  error?: string
  touched?: boolean
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  onChange: (field: string, value: string) => void
  onBlur?: (field: string) => void
}

export const DateInput: React.FC<DateInputProps> = ({
  id,
  name,
  label,
  labelClassName,
  value,
  error,
  touched,
  placeholder = 'MM/DD/YYYY',
  disabled,
  required,
  readOnly,
  onChange,
  onBlur,
}) => {
  const [displayValue, setDisplayValue] = React.useState('')
  const pickerRef = React.useRef<any>(null)

  React.useEffect(() => {
    if (!value) {
      if (displayValue !== '') setDisplayValue('')
      return
    }
    // If value is ISO (YYYY-MM-DD), convert to display MM/DD/YYYY; otherwise assume it's already display
    const formatted = value.includes('-')
      ? formatDateForSave(parseISOToDate(value)!).display
      : value
    if (formatted !== displayValue) setDisplayValue(formatted)
  }, [value])

  const handleManualInputChange = (
    event?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
  ) => {
    const target = event?.target as HTMLInputElement | null;
    // 🧩 Evitar error cuando se dispara desde el calendario
    if (!target || typeof target.value !== 'string') {
      return;
    }
    let input = target.value;
    const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    input = input.replace(/\D/g, '')
    if (input.length > 8) input = input.slice(0, 8)
    if (input.length > 4) input = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4)}`
    else if (input.length > 2) input = `${input.slice(0, 2)}/${input.slice(2)}`
    setDisplayValue(input)
    if (input.length === 10 && mmddyyyyRegex.test(input)) {
      const [mm, dd, yyyy] = [input.slice(0, 2), input.slice(3, 5), input.slice(6, 10)]
      const iso = `${yyyy}-${mm}-${dd}`
      if (iso !== value) onChange(name, iso)
    }
  }

  return (
    <Form.Group>
      {label && (
        <Form.Label htmlFor={id} className={labelClassName}>
          {label}
        </Form.Label>
      )}
      <InputGroup className="w-100 flex-nowrap">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <DatePicker
            ref={pickerRef}
            selected={parseISOToDate(value)}
            onChange={(date: Date | null) => {
              if (!date) {
                if (displayValue !== '') setDisplayValue('')
                onChange(name, null as any)
                return
              }
              const { display, iso } = formatDateForSave(date)
              if (displayValue !== display) setDisplayValue(display)
              if (iso !== value) onChange(name, iso)
            }}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            disabled={disabled}
            readOnly={readOnly}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            wrapperClassName="w-100"
            value={displayValue} // 🔹 fuerza a usar el valor del estado, no el interno de react-datepicker
            onChangeRaw={handleManualInputChange} // 🔹 usa onChangeRaw, no customInput.onChange
            popperPlacement="bottom-start"
            popperContainer={({ children }) => (
              <div style={{ position: 'absolute', zIndex: 1060 }}>{children}</div>
            )}
            popperProps={{
              strategy: 'fixed',
            }}
            customInput={
              <Form.Control
                type="text"
                id={id}
                name={name}
                placeholder={placeholder}
                value={displayValue}
                onBlur={() => onBlur && onBlur(name)}
                isInvalid={!!error && !!touched}
                required={required}
              />
            }
          />
        </div>
        <InputGroup.Text
          role="button"
          title="Open calendar"
          className="bg-white"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (pickerRef.current?.setOpen) {
              pickerRef.current.setOpen(true)
            }
          }}
        >
          <i className="bi bi-calendar"></i>
        </InputGroup.Text>
      </InputGroup>
      {error && touched && (
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      )}
    </Form.Group>
  )
}
